import type { ThreadEntry } from "../../../api/threads/liveMessages";

/** The longest span a memory source may have (memory.md MEM-9 `maximum_span_characters`). */
export const MAXIMUM_PASSAGE_CHARACTERS = 4000;

/** A passage of an entry in the form the server anchors: the exact text, and which appearance of it. */
export interface Passage {
  readonly quote: string;
  readonly occurrence: number;
}

export type PassageLookup =
  | { readonly status: "found"; readonly passage: Passage }
  /** The selected text is not the entry's own text (it was drawn from several blocks, or a link or table). */
  | { readonly status: "not_in_entry" }
  | { readonly status: "too_long" };

/**
 * The text of an entry a memory may quote: a message, an agent's answer (its last message)
 * or a human step's comment; `null` for every other entry (memory.md MEM-1, the server's
 * `source_text`).
 */
export function quotableText(entry: ThreadEntry): string | null {
  const payload = entry.payload;
  if (payload.kind === "message") {
    return payload.text;
  }
  if (payload.kind === "turn_finished") {
    return payload.agent_messages.at(-1) ?? null;
  }
  if (payload.kind === "human_input") {
    return payload.comment === "" ? null : payload.comment;
  }
  return null;
}

const MARK = "[*_`]*";
const BETWEEN_WORDS = "[\\s*_`>#-]+";

function escapeRegularExpression(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Starts of `text` in `entryText`, overlapping ones included, as the server counts them. */
function startsOf(entryText: string, text: string): number[] {
  const starts: number[] = [];
  for (let at = entryText.indexOf(text); at >= 0; at = entryText.indexOf(text, at + 1)) {
    starts.push(at);
  }
  return starts;
}

/** The raw spans that read as `selected` once Markdown marks are not shown, in order. */
function spansReadingAs(entryText: string, selected: string): { start: number; quote: string }[] {
  const words = selected.split(/\s+/).filter((word) => word !== "");
  const pattern = words.map((word) => Array.from(word, escapeRegularExpression).join(MARK)).join(BETWEEN_WORDS);
  const spans: { start: number; quote: string }[] = [];
  const matcher = new RegExp(pattern, "gu");
  for (let match = matcher.exec(entryText); match !== null; match = matcher.exec(entryText)) {
    spans.push({ start: match.index, quote: match[0] });
    matcher.lastIndex = match.index + 1;
  }
  return spans;
}

/**
 * Where `selected`, text a person marked in the drawn entry, lies in the entry's own text.
 * The drawn text has no Markdown marks and its lines wrap, so an exact match is tried first,
 * then one that ignores marks and white space between words; the quote returned is always the
 * entry's own text, so the server finds it. `earlierAppearances` is how many times the same
 * selected text already appeared in the drawn entry before the selection, to pick the one
 * that was marked.
 */
export function locatePassage(entryText: string, selected: string, earlierAppearances: number): PassageLookup {
  const text = selected.trim();
  if (text === "") {
    return { status: "not_in_entry" };
  }
  const exact = startsOf(entryText, text).map((start) => ({ start, quote: text }));
  const spans = exact.length > 0 ? exact : spansReadingAs(entryText, text);
  const chosen = spans[Math.min(earlierAppearances, spans.length - 1)];
  if (chosen === undefined) {
    return { status: "not_in_entry" };
  }
  if (chosen.quote.length > MAXIMUM_PASSAGE_CHARACTERS) {
    return { status: "too_long" };
  }
  const occurrence = startsOf(entryText, chosen.quote).filter((start) => start <= chosen.start).length;
  return { status: "found", passage: { quote: chosen.quote, occurrence } };
}
